import { ActivityFillCell } from "#/components/ui/activity-fill-cell";
import {
  profileActivityFillLabel,
  resolveActivityFillFromCalendarDay,
} from "#/lib/calendarHeatmap";
import { cn } from "#/lib/utils";
import type { CalendarMonthDay } from "#/server/game/archiveCalendarMonth";

function daysInMonth(monthKey: string): number {
  const year = Number(monthKey.slice(0, 4));
  const month = Number(monthKey.slice(5, 7));
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function calendarDateForDay({
  monthKey,
  dayNumber,
}: {
  monthKey: string;
  dayNumber: number;
}): string {
  return `${monthKey}-${String(dayNumber).padStart(2, "0")}`;
}

function dayNumberClassName({ dateStr, todayStr }: { dateStr: string; todayStr: string }): string {
  if (dateStr === todayStr) return "font-semibold text-foreground";
  return "text-muted-foreground";
}

function CalendarDayHeatmapCell({
  dayNumber,
  todayStr,
  monthKey,
  day,
}: {
  dayNumber: number;
  todayStr: string;
  monthKey: string;
  day?: CalendarMonthDay;
}) {
  const dateStr = calendarDateForDay({ monthKey, dayNumber });
  const fill = resolveActivityFillFromCalendarDay({ date: dateStr, day });
  const statusLabel = !day && fill === "empty" ? "No maps" : profileActivityFillLabel(fill);
  const title = `Day ${dayNumber}: ${statusLabel}`;

  return (
    <div className="flex flex-col items-center gap-1" title={title} aria-label={title}>
      <span
        className={cn(
          "text-[11px] leading-none font-medium tabular-nums",
          dayNumberClassName({ dateStr, todayStr }),
        )}
      >
        {dayNumber}
      </span>
      <ActivityFillCell fill={fill} size="lg" />
    </div>
  );
}

export function CalendarMonthHeatmap({
  monthKey,
  todayStr,
  days,
}: {
  monthKey: string;
  todayStr: string;
  days: CalendarMonthDay[];
}) {
  const daysByNumber = new Map(days.map((day) => [day.dayNumber, day]));
  const totalDays = daysInMonth(monthKey);

  return (
    <div className="mb-4">
      <div className="flex flex-wrap justify-center gap-1.5">
        {Array.from({ length: totalDays }, (_, index) => {
          const dayNumber = index + 1;
          return (
            <CalendarDayHeatmapCell
              key={dayNumber}
              dayNumber={dayNumber}
              todayStr={todayStr}
              monthKey={monthKey}
              day={daysByNumber.get(dayNumber)}
            />
          );
        })}
      </div>
    </div>
  );
}
